import { useEffect, useRef } from "react";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment";
import { MONOGRAM_MAIN, MONOGRAM_ACCENT } from "./AMBLogo";
import { HERO_TOOLS } from "../data/tools";

const buildMonogram = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 620 200"><path fill-rule="evenodd" d="${MONOGRAM_MAIN}"/><path d="${MONOGRAM_ACCENT}"/></svg>`;
  const data = new SVGLoader().parse(svg);
  const metal = new THREE.MeshStandardMaterial({ color: 0xe8edf3, metalness: 0.95, roughness: 0.28 });
  const blue = new THREE.MeshStandardMaterial({
    color: 0x000a2a, metalness: 0.3, roughness: 0.5, emissive: 0x0043b2, emissiveIntensity: 0.7, // mesmo azul do logo do cabeçalho (#1e8bff → #0043b2)
  });
  const inner = new THREE.Group();
  data.paths.forEach((path, i) => {
    SVGLoader.createShapes(path).forEach((shape) => {
      const geo = new THREE.ExtrudeGeometry(shape, {
        depth: 22, bevelEnabled: true, bevelThickness: 3.5, bevelSize: 3, bevelSegments: 3, curveSegments: 6,
      });
      const mesh = new THREE.Mesh(geo, i === 1 ? blue : metal);
      if (i === 1) mesh.position.z = -2; // acento azul fica coplanar com o cromado e some; empurra pra frente (z invertido pela rotação)
      inner.add(mesh);
    });
  });
  inner.rotation.x = Math.PI;
  const outer = new THREE.Group();
  outer.add(inner);
  const box = new THREE.Box3().setFromObject(outer);
  const size = box.getSize(new THREE.Vector3());
  outer.scale.setScalar(6.1 / size.x);
  const box2 = new THREE.Box3().setFromObject(outer);
  outer.position.sub(box2.getCenter(new THREE.Vector3()));
  const pivot = new THREE.Group();
  pivot.add(outer);
  return pivot;
};

const buildParticles = (count) => {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 30;
    pos[i * 3 + 1] = (Math.random() - 0.5) * 16;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 12 - 2;
  }
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  const group = new THREE.Group();
  group.add(new THREE.Points(geo, new THREE.PointsMaterial({
    color: 0x2f8bff, size: 0.055, transparent: true, opacity: 0.85,
    blending: THREE.AdditiveBlending, depthWrite: false,
  })));
  group.add(new THREE.Points(geo.clone(), new THREE.PointsMaterial({
    color: 0xf8fafc, size: 0.03, transparent: true, opacity: 0.45,
    blending: THREE.AdditiveBlending, depthWrite: false,
  })));
  return group;
};

const makeBadge = ({ slug, color }) =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      const glow = ctx.createRadialGradient(128, 128, 40, 128, 128, 118);
      glow.addColorStop(0, `${color}33`);
      glow.addColorStop(1, `${color}00`);
      ctx.beginPath();
      ctx.arc(128, 128, 118, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(11,15,20,0.94)";
      ctx.fill();
      ctx.fillStyle = glow;
      ctx.fill();
      ctx.lineWidth = 5;
      ctx.strokeStyle = color;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(128, 128, 104, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.stroke();
      ctx.drawImage(img, 70, 70, 116, 116);
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      resolve(tex);
    };
    img.onerror = () => resolve(null);
    img.src = `/logos/color/${slug}.svg`;
  });

const buildOrbit = (tools, isMobile) => {
  const group = new THREE.Group();
  const base = isMobile ? 0.44 : 0.66;
  const items = tools.map((tool, i) => {
    const mat = new THREE.SpriteMaterial({ transparent: true, opacity: 0, depthWrite: false, fog: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(base, base, 1);
    group.add(sprite);
    const item = {
      sprite, mat, ready: false,
      phase: (i / tools.length) * Math.PI * 2,
      r: (isMobile ? 1.0 : 2.5) + (i % 4) * (isMobile ? 0.15 : 0.4),
      zr: isMobile ? 2.4 + (i % 3) * 0.4 : 0,
      speed: 0.1 + (i % 4) * 0.02,
      yAmp: 0.6 + (i % 3) * 0.35,
    };
    makeBadge(tool).then((tex) => {
      if (!tex) return;
      mat.map = tex;
      mat.needsUpdate = true;
      item.ready = true;
    });
    return item;
  });
  const update = (t, scroll, pointer) => {
    const spread = 1 + Math.min(scroll, 1.2) * 1.1;
    items.forEach((it) => {
      const a = it.phase + t * it.speed * (1 + scroll * 0.9) + pointer.x * 0.25;
      const r = it.r * spread;
      it.sprite.position.set(
        Math.cos(a) * r + (isMobile ? 0 : 0.3),
        Math.sin(a * 1.6 + it.phase) * it.yAmp * 0.5 + pointer.y * 0.2,
        Math.sin(a) * (isMobile ? it.zr : r * 0.75)
      );
      const depth = (Math.sin(a) + 1) / 2;
      const target = it.ready ? 0.3 + depth * 0.7 : 0;
      it.mat.opacity += (target - it.mat.opacity) * 0.08;
      const sc = base * (0.7 + depth * 0.45);
      it.sprite.scale.set(sc, sc, 1);
    });
  };
  return { group, update };
};

// layout: "split" = monograma à direita (home); "center" = monograma centralizado acima do título (/3)
export const Scene3D = ({ layout = "split" }) => {
  const mountRef = useRef(null);
  const dimRef = useRef(null);

  useEffect(() => {
    try {
      const mount = mountRef.current;
      const dim = dimRef.current;
      if (!mount) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x05070d, 0.05);
    const camera = new THREE.PerspectiveCamera(38, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0.3, 9.2);

    const renderer = new THREE.WebGLRenderer({ antialias: !isMobile, alpha: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const key = new THREE.PointLight(0x007bff, 220, 60, 2);
    key.position.set(7, 5, 6);
    scene.add(key);
    const fill = new THREE.PointLight(0xf8fafc, 70, 50, 2);
    fill.position.set(-6, -2, 4);
    scene.add(fill);
    const rim = new THREE.PointLight(0x1e8bff, 140, 50, 2);
    rim.position.set(0, -6, -4);
    scene.add(rim);

    const monogram = buildMonogram();
    const center = layout === "center";
    const wide = !center && window.innerWidth >= 1280; // hero com painel de áreas à direita: monograma vai pro meio
    const baseX = isMobile || center ? 0 : wide ? 1.1 : 2.7;
    const baseY = isMobile ? 1.4 : center ? 1.6 : -0.25;
    if (isMobile) monogram.scale.setScalar(0.47);
    else if (center) monogram.scale.setScalar(0.52);
    else if (wide) monogram.scale.setScalar(0.62);
    monogram.position.set(baseX, baseY, 0);
    scene.add(monogram);

    const orbit = buildOrbit(isMobile ? [...HERO_TOOLS.slice(0, 5), HERO_TOOLS[0], ...HERO_TOOLS.slice(5, 9)] : HERO_TOOLS, isMobile);
    orbit.group.position.copy(monogram.position);
    scene.add(orbit.group);

    const particles = buildParticles(isMobile ? 140 : 420);
    scene.add(particles);

    const pointer = { x: 0, y: 0 };
    const onPointer = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      if (reduced) renderer.render(scene, camera);
    };
    window.addEventListener("pointermove", onPointer, { passive: true });
    window.addEventListener("resize", onResize);

    let raf = 0;
    if (reduced) {
      monogram.rotation.set(-0.08, 0.35, 0);
      orbit.update(0, 0, pointer);
      const onScroll = () => {
        const p = Math.min(window.scrollY / window.innerHeight, 1);
        if (dim) dim.style.opacity = (p * 0.5).toFixed(3);
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      const check = () => {
        renderer.render(scene, camera);
        raf = requestAnimationFrame(check);
      };
      raf = requestAnimationFrame(check);
      setTimeout(() => cancelAnimationFrame(raf), 2500);
    } else {
      const clock = new THREE.Clock();
      const animate = () => {
        raf = requestAnimationFrame(animate);
        const t = clock.getElapsedTime();
        const sy = window.scrollY;
        const vh = window.innerHeight;
        const p = Math.min(sy / vh, 1);
        const total = sy / Math.max(1, document.documentElement.scrollHeight - vh);

        const targetRotY = Math.sin(t * 0.18) * 0.22 + pointer.x * 0.4 + sy * 0.0011;
        monogram.rotation.y += (targetRotY - monogram.rotation.y) * 0.05;
        monogram.rotation.x += (pointer.y * 0.22 + p * 0.28 - monogram.rotation.x) * 0.05;
        monogram.position.x += (baseX * (1 - p) - monogram.position.x) * 0.06;
        monogram.position.z += (-p * 3.4 - total * 2.5 - monogram.position.z) * 0.06;
        monogram.position.y = baseY * (1 - p) + Math.sin(t * 0.7) * 0.12 + (isMobile ? 0 : p * 0.3);

        orbit.group.position.lerp(monogram.position, 0.08);
        orbit.update(t, p + total * 0.4, pointer);

        particles.rotation.y = t * 0.02 + pointer.x * 0.06;
        particles.rotation.x = pointer.y * 0.03 + sy * 0.00012;
        particles.position.y = sy * 0.0009;
        camera.position.y = 0.3 - p * 0.5;
        key.intensity = 220 + Math.sin(t * 1.6) * 40;
        if (dim) dim.style.opacity = (p * 0.5).toFixed(3);
        renderer.render(scene, camera);
      };
      animate();
    }

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", onPointer);
        window.removeEventListener("resize", onResize);
        renderer.dispose();
        pmrem.dispose();
        scene.traverse((o) => {
          if (o.geometry) o.geometry.dispose();
          if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => { m.map?.dispose(); m.dispose(); });
        });
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    } catch (err) {
      console.error("Scene3D error:", err);
    }
  }, [layout]);

  return (
    <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
      <div ref={mountRef} data-testid="scene-3d-canvas" className="absolute inset-0" />
      <div ref={dimRef} data-testid="scene-3d-dim" className="absolute inset-0 bg-[#05070D]" style={{ opacity: 0 }} />
    </div>
  );
};
